import { clsx } from "clsx";
import {
  format,
  isWithinInterval,
  differenceInDays,
  isAfter,
  isBefore,
  isSameDay,
  addDays,
  differenceInCalendarDays,
} from "date-fns";
import classes from "./RenderMonth.module.scss";
import {
  oneLetterWeekDayNames,
  shortWeekDayNames,
  weekDayNames,
} from "../../../../../../../utils/constants/periodLists";
import {
  IBasicTooltip,
  ICalendarMonthView,
  IReservation,
} from "../../../../../../interfaces";
import { Legend } from "../../../../../../common/ChartLegend/ChartLegend";
import { getDates } from "../../../../../../../utils/calendarUtils";
import { useSelector } from "react-redux";
import { allReservations } from "../../../../../../../redux/selectors/reservationsData";
import { ClickAwayListener, IconButton, Tooltip } from "@mui/material";
import { LightInfoIcon } from "../../../../../../../assets/icons/LightInfoIcon";
import { AirbnbIcon } from "../../../../../../../assets/icons/AirbnbIcon";
import { BookingIcon } from "../../../../../../../assets/icons/BookingIcon";
import { ExpediaIcon } from "../../../../../../../assets/icons/ExpediaIcon";
import { VrboIcon } from "../../../../../../../assets/icons/VrboIcon";
import { isMobileModeSelector } from "../../../../../../../redux/selectors/appStatusSelector";
import { calendarLegend } from "../../Calendar";
import { useState } from "react";
import { toPrettyNumber } from "../../../../../../../utils/commonUtils";
import { Typography } from "../../../../../../../shared/ui/typography";
import { LockIcon } from "@/assets/icons/LockIcon";

const channelToIconMap = (channel: string) => {
  switch (channel) {
    case "airbnb2": {
      return <AirbnbIcon />;
    }
    case "Booking.com":
    case "Booking Engine": {
      return <BookingIcon />;
    }
    case "HomeAway CA":
    case "VRBO": {
      return <VrboIcon />;
    }
    case "expedia": {
      return <ExpediaIcon />;
    }
    case "owner": {
      return <LockIcon />;
    }
    default: {
      return "";
    }
  }
};

const ChannelIcon = ({
  isCurrentDayPaidAndUpcoming,
  isCurrentDayOwnerAndUpcoming,
  isCurrentDayPaidAndPrevious,
  currentDayCheckingIcon,
}: {
  isCurrentDayPaidAndUpcoming: boolean;
  isCurrentDayOwnerAndUpcoming: boolean;
  isCurrentDayPaidAndPrevious: boolean;
  currentDayCheckingIcon: JSX.Element | string;
}) => <div className={`${classes.sourceIcon}`}>{currentDayCheckingIcon}</div>;

const CustomTooltip = ({ tooltip, className }: IBasicTooltip) => {
  const [open, setOpen] = useState(false);

  const handleTooltipClose = () => {
    setOpen(false);
  };

  const handleTooltipOpen = () => {
    setOpen(true);
  };

  return (
    <ClickAwayListener onClickAway={handleTooltipClose}>
      <Tooltip
        arrow
        className={className}
        classes={{ tooltip: classes.tooltip, arrow: classes.tooltipArrow }}
        placement="top"
        title={tooltip}
        open={open}
      >
        <IconButton
          onClick={handleTooltipOpen}
          onMouseOver={handleTooltipOpen}
          onMouseOut={handleTooltipClose}
        >
          <LightInfoIcon />
        </IconButton>
      </Tooltip>
    </ClickAwayListener>
  );
};

const getTooltipContent = (currentDayChecking: any) =>
  currentDayChecking?.created_by
    ? `${
        currentDayChecking?.property_id
          ? `${currentDayChecking?.property_id} - `
          : ""
      }${currentDayChecking?.created_by}${
        currentDayChecking?.guest_count
          ? ` - ${currentDayChecking?.guest_count} guest${
              currentDayChecking?.guest_count > 1 ? "s" : ""
            }`
          : ""
      } amount $${
        toPrettyNumber(currentDayChecking?.netincome) ||
        toPrettyNumber(currentDayChecking?.netincome_usd)
      }`
    : `${
        currentDayChecking?.property_id
          ? `${currentDayChecking?.property_id} - `
          : ""
      }${currentDayChecking?.guest_name}${
        currentDayChecking?.guest_count
          ? ` - ${currentDayChecking?.guest_count} guest${
              currentDayChecking?.guest_count > 1 ? "s" : ""
            }`
          : ""
      } amount $${
        toPrettyNumber(currentDayChecking?.netincome) ||
        toPrettyNumber(currentDayChecking?.netincome_usd)
      }`;

const getOwnerBlockName = (name?: string) => {
  const nameSplitByHyphen = name?.split("-")?.[1];
  const nameSplitBySpace = nameSplitByHyphen?.split(" ")?.filter((v) => !!v);

  return `${nameSplitBySpace?.[0] ? nameSplitBySpace?.[0] : "Owner"} ${
    nameSplitBySpace?.[1] ? nameSplitBySpace?.[1] : "Block"
  }`;
};

export const ReservationDetails = ({
  currentDayChecking,
  isCurrentDayPaidAndUpcoming,
  isCurrentDayOwnerAndUpcoming,
}: {
  currentDayChecking: any;
  isCurrentDayPaidAndUpcoming: boolean;
  isCurrentDayOwnerAndUpcoming: boolean;
}) => {
  return (
    <div>
      <Typography variant="text-md" weight="medium" className={classes.name}>
        {currentDayChecking?.guest_name?.split(" ")?.[0]}
      </Typography>
      <Typography
        variant="text-sm"
        weight="regular"
        className={clsx(classes.name, classes.guests, {
          [classes.upcoming]:
            isCurrentDayPaidAndUpcoming || isCurrentDayOwnerAndUpcoming,
          [classes.previous]: !(
            isCurrentDayPaidAndUpcoming || isCurrentDayOwnerAndUpcoming
          ),
        })}
      >
        {/* TODO label  */}
        <span>
          {!currentDayChecking.is_pricelab && (
            <>
              {currentDayChecking?.guest_count
                ? `${currentDayChecking.guest_count} ${
                    currentDayChecking.guest_count > 1 ? "guests" : "guest"
                  }`
                : ""}
              {" • "}
            </>
          )}
          {`${
            toPrettyNumber(currentDayChecking?.netincome) ||
            toPrettyNumber(currentDayChecking?.netincome_usd)
          }`}
        </span>
      </Typography>
    </div>
  );
};

export const RenderMonth = ({
  calendarData,
  currentMonth,
  currentYear,
  reservationsData,
  small,
  year,
  onClick,
  isLeftSide,
}: ICalendarMonthView) => {
  const allReservationsData = useSelector(allReservations);
  const isMobileView = useSelector(isMobileModeSelector);
  
  // const dateList = calendarData
  //   .flat()
  //   .map((date: Date) => format(date, "yyyy-MM-dd"));
  const dateList = calendarData
  .flat() // flatten weeks → dates
  .filter((d: Date | null | undefined) => d instanceof Date && !isNaN(d.getTime())) // keep only valid dates
  .map((date: Date) => format(date, "yyyy-MM-dd"));
    // console.log("dateList: ", dateList);
  const currentMonthDateMap = dateList.reduce(
    (obj: any, date: any) => ({
      [date]: allReservationsData
        ?.filter((reservation: any) => reservation.status !== "canceled")
        .filter((reservation: any) =>
          isWithinInterval(new Date(date), {
            start: new Date(reservation.checkin_at.split(" ")[0]),
            end: new Date(reservation.checkout_at.split(" ")[0]),
          })
        ),
      ...obj,
    }),
    {}
  );

  const weekDays =
    small && isMobileView
      ? oneLetterWeekDayNames
      : small || isMobileView
      ? shortWeekDayNames
      : weekDayNames.map((day) => day.toUpperCase());
  const reservationsDataForCurrentMonth =
    reservationsData?.[currentYear]?.[currentMonth];
  const datesWithReservations: any = {};

  if (reservationsDataForCurrentMonth) {
    Object.values(reservationsDataForCurrentMonth).forEach(
      (reservation: any) => {
        const startReservDay = new Date(reservation.checkin_at);
        const endReservDay = new Date(reservation.checkout_at);
        const rangeDates = getDates(startReservDay, endReservDay);
        rangeDates.forEach((date) => {
          datesWithReservations[date.getDate()] = reservation;
        });
      }
    );
  }

  return (
    <>
      <div
        className={`${classes.monthBody} ${small && classes.small} ${
          isLeftSide && classes.leftSide
        }`}
        onClick={onClick}
      >
        <div
          className={`${classes.dayWeekSection} ${
            isLeftSide && classes.leftSide
          }`}
        >
          {weekDays.map((dayName, index) => (
            <div
              className={clsx(
                classes.dayWeek,
                small && classes.smallDayWeek,
                !small && classes.bigDayWeek
              )}
              key={`${dayName}_${index}`}
            >
              {dayName}
            </div>
          ))}
        </div>
        <div className={classes.month}>
          {calendarData.map((week: any) => (
            <div
              className={`${classes.weekSection} ${
                isLeftSide && classes.leftSide
              }`}
              key={week}
            >
              {week.map((day: Date | null | undefined, dayIndex: number) => {
                if (!(day instanceof Date) || isNaN(day.getTime())) {
                  return ''; // render empty cell for invalid dates
                }
                const today = new Date();
                const currentDay = format(day, "yyyy-MM-dd");
                const currentDayReservations = currentMonthDateMap[currentDay];
                const currentDayChecking = currentDayReservations?.find(
                  (reservation: any) =>
                    reservation.checkin_at.split(" ")?.[0] === currentDay
                );
                const ownerBlockName = getOwnerBlockName(
                  currentDayChecking?.created_by
                );
                const dayNumber = format(day, "d");
                const isPaid = currentDayReservations?.some(
                  (reservation: any) => reservation?.netincome
                );
                const isPaidCheckin = currentDayReservations?.find(
                  (reservation: any) =>
                    reservation.checkin_at.split(" ")?.[0] === currentDay
                )?.netincome;
                const isPaidCheckout = currentDayReservations?.find(
                  (reservation: any) =>
                    reservation.checkout_at.split(" ")?.[0] === currentDay
                )?.netincome;
                const isFirstOfRange =
                  !small &&
                  currentDayReservations?.some(
                    (reservation: any) =>
                      reservation.checkin_at.split(" ")[0] === currentDay
                  );
                const isSecondOfRange =
                  !small &&
                  currentDayReservations?.length < 2 &&
                  currentDayReservations?.some(
                    (reservation: any) =>
                      format(
                        addDays(new Date(reservation.checkin_at), 1),
                        "yyyy-MM-dd"
                      ) === currentDay
                  );
                const isLastOfRange =
                  !small &&
                  currentDayReservations?.some(
                    (reservation: any) =>
                      reservation.checkout_at.split(" ")[0] === currentDay
                  );
                const isCurrentDayPaidAndUpcoming =
                  (currentDayReservations?.length === 1 &&
                    isPaid &&
                    new Date(
                      currentDayReservations[0].checkout_at.split(" ")[0]
                    ) >= today) ||
                  (currentDayReservations?.length > 1 &&
                    isPaidCheckin &&
                    new Date(
                      currentDayReservations
                        ?.find(
                          (reservation: any) =>
                            reservation.checkin_at.split(" ")?.[0] ===
                            currentDay
                        )
                        ?.checkout_at.split(" ")[0]
                    ) >= today);

                const isCurrentDayOwnerAndUpcoming =
                  (currentDayReservations?.length === 1 &&
                    !isPaid &&
                    new Date(
                      currentDayReservations[0].checkout_at.split(" ")[0]
                    ) >= today) ||
                  (currentDayReservations?.length > 1 &&
                    !isPaidCheckin &&
                    new Date(
                      currentDayReservations
                        ?.find(
                          (reservation: any) =>
                            reservation.checkin_at.split(" ")?.[0] ===
                            currentDay
                        )
                        ?.checkout_at.split(" ")[0]
                    ) >= today);

                const isCurrentDayPaidAndPrevious =
                  (currentDayReservations?.length === 1 &&
                    isPaid &&
                    new Date(
                      currentDayReservations[0].checkout_at.split(" ")[0]
                    ) < today) ||
                  (currentDayReservations?.length > 1 &&
                    isPaidCheckin &&
                    new Date(
                      currentDayReservations
                        ?.find(
                          (reservation: any) =>
                            reservation.checkin_at.split(" ")?.[0] ===
                            currentDay
                        )
                        ?.checkout_at.split(" ")[0]
                    ) < today);

                const currentDayCheckingIcon = channelToIconMap(
                  currentDayChecking?.source
                );

                const firstReservation = currentDayReservations?.[0];

                const checkoutDateFirst = firstReservation
                  ? new Date(firstReservation.checkout_at)
                  : null;

                const foundReservation = currentDayReservations?.find(
                  (reservation: any) => reservation.checkout_at.split(" ")?.[0]
                );

                const checkoutDateFound = foundReservation
                  ? new Date(foundReservation.checkout_at)
                  : null;

                const todayReservation = currentDayReservations?.find(
                  (reservation: any) =>
                    reservation.checkout_at.split(" ")?.[0] === currentDay
                );

                const todayCheckinDate = todayReservation
                  ? new Date(todayReservation.checkin_at.split(" ")[0])
                  : null;

                const todayCheckoutDate = todayReservation
                  ? new Date(todayReservation.checkout_at.split(" ")[0])
                  : null;

                return (
                  <div
                    style={{
                      position: "relative",
                      visibility:
                        small && currentMonth !== day.getMonth()
                          ? "hidden"
                          : "visible",
                    }}
                    className={clsx(classes.day, {
                      [classes.pastDay]:
                        isBefore(new Date(day), today) &&
                        !isSameDay(new Date(day), today),
                      [classes.smallDay]: small,
                      [classes.bigDay]: !small,
                    })}
                    key={dayNumber}
                  >
                    <p
                      //todo refactor this
                      className={`${
                        format(day, "yyyy-MM-dd") ===
                        format(today, "yyyy-MM-dd")
                          ? small
                            ? isLeftSide
                              ? classes.todayDateSmallLeftSide
                              : classes.todayDateSmall
                            : classes.todayDate
                          : undefined
                      } ${isLeftSide && classes.leftSide}`}
                    >
                      {format(day, "dd")}
                    </p>
                    {small && currentDayReservations?.length ? (
                      <div className={classes.circleBlock}>
                        {currentDayReservations.length &&
                          currentDayReservations
                            ?.sort(
                              (first: IReservation, second: IReservation) =>
                                isAfter(
                                  new Date(first.checkin_at),
                                  new Date(second.checkin_at)
                                )
                                  ? 1
                                  : -1
                            )
                            .map((reservation: IReservation) => (
                              <span
                                key={reservation?.reservation_id}
                                className={`${classes.circle} ${
                                  (reservation.is_owner_block == false)
                                    ? classes.paid
                                    : classes.owner
                                }`}
                              />
                            ))}
                      </div>
                    ) : null}
                    {!small && currentDayReservations?.length > 0 && (
                      <>
                        <div
                          className={clsx(classes.reservation, {
                            [classes.beginning]: isFirstOfRange,
                            [classes.ending]: isLastOfRange,
                            [classes.paid]: isCurrentDayPaidAndUpcoming,
                            [classes.secondDay]: isSecondOfRange,
                            [classes.lastDay]: dayIndex === 6,
                            [classes.owner]:
                              !isCurrentDayPaidAndUpcoming &&
                              isCurrentDayOwnerAndUpcoming,
                            [classes.paidPrevious]:
                              !isCurrentDayPaidAndUpcoming &&
                              !isCurrentDayOwnerAndUpcoming &&
                              isCurrentDayPaidAndPrevious,
                            [classes.ownerPrevious]:
                              !isCurrentDayPaidAndUpcoming &&
                              !isCurrentDayOwnerAndUpcoming &&
                              !isCurrentDayPaidAndPrevious,
                            [classes.bothBeginning]:
                              currentDayReservations?.length > 1,
                            [classes.paidCurrent]:
                              (currentDayReservations?.length === 1 &&
                                isPaid &&
                                new Date(
                                  currentDayReservations[0].checkout_at.split(
                                    " "
                                  )[0]
                                ) > today &&
                                new Date(
                                  currentDayReservations[0].checkin_at.split(
                                    " "
                                  )[0]
                                ) <= today) ||
                              (currentDayReservations.length > 1 &&
                                isPaidCheckin &&
                                new Date(
                                  currentDayReservations
                                    ?.find(
                                      (reservation: any) =>
                                        reservation.checkin_at.split(
                                          " "
                                        )?.[0] === currentDay
                                    )
                                    ?.checkout_at.split(" ")[0]
                                ) > today &&
                                new Date(
                                  currentDayReservations
                                    ?.find(
                                      (reservation: any) =>
                                        reservation.checkin_at.split(
                                          " "
                                        )?.[0] === currentDay
                                    )
                                    ?.checkin_at.split(" ")[0]
                                ) <= today),
                            [classes.ownerCurrent]:
                              (currentDayReservations?.length === 1 &&
                                !isPaid &&
                                new Date(
                                  currentDayReservations[0].checkout_at.split(
                                    " "
                                  )[0]
                                ) > today &&
                                new Date(
                                  currentDayReservations[0].checkin_at.split(
                                    " "
                                  )[0]
                                ) <= today) ||
                              (currentDayReservations.length > 1 &&
                                !isPaidCheckin &&
                                new Date(
                                  currentDayReservations
                                    ?.find(
                                      (reservation: any) =>
                                        reservation.checkin_at.split(
                                          " "
                                        )?.[0] === currentDay
                                    )
                                    ?.checkout_at.split(" ")[0]
                                ) > today &&
                                new Date(
                                  currentDayReservations
                                    ?.find(
                                      (reservation: any) =>
                                        reservation.checkin_at.split(
                                          " "
                                        )?.[0] === currentDay
                                    )
                                    ?.checkin_at.split(" ")[0]
                                ) <= today),
                          })}
                        >
                          {isFirstOfRange && (
                            <div>
                              {dayIndex < 6 ? (
                                <ChannelIcon
                                  isCurrentDayPaidAndUpcoming={
                                    isCurrentDayPaidAndUpcoming
                                  }
                                  isCurrentDayOwnerAndUpcoming={
                                    isCurrentDayOwnerAndUpcoming
                                  }
                                  isCurrentDayPaidAndPrevious={
                                    isCurrentDayPaidAndPrevious
                                  }
                                  currentDayCheckingIcon={
                                    currentDayCheckingIcon
                                  }
                                />
                              ) : null}
                              {differenceInDays(
                                new Date(currentDayChecking?.checkout_at),
                                new Date(currentDayChecking?.checkin_at)
                              ) < 3 && dayIndex > 4 ? (
                                <div
                                  className={clsx({
                                    [classes.tooltipIcon]: dayIndex < 6,
                                    [classes.noSourceIcon]: dayIndex >= 6,
                                    [classes.withSourceIcon]:
                                      !!currentDayCheckingIcon,
                                  })}
                                >
                                  {dayIndex === 6 &&
                                    differenceInDays(
                                      new Date(currentDayChecking?.checkout_at),
                                      new Date(currentDayChecking?.checkin_at)
                                    ) < 2 && (
                                      <CustomTooltip
                                        className={`${
                                          isCurrentDayPaidAndUpcoming
                                            ? classes.paidSvgFill
                                            : isCurrentDayOwnerAndUpcoming
                                            ? classes.ownerSvgFill
                                            : isCurrentDayPaidAndPrevious
                                            ? classes.paidPreviousSvgFill
                                            : classes.ownerPreviousSvgFill
                                        }`}
                                        tooltip={getTooltipContent(
                                          currentDayChecking
                                        )}
                                      />
                                    )}
                                </div>
                              ) : (
                                <div
                                  className={clsx(classes.reservationContent, {
                                    // [classes.noSourceIcon]:
                                    //   !isPaidCheckin || !currentDayCheckingIcon,
                                  })}
                                >
                                  {!isPaidCheckin ? (
                                    differenceInDays(
                                      new Date(currentDayChecking?.checkout_at),
                                      new Date(currentDayChecking?.checkin_at)
                                    ) < 2 ? (
                                      <div
                                        style={{
                                          marginLeft:
                                            differenceInDays(
                                              new Date(
                                                currentDayChecking?.checkout_at
                                              ),
                                              new Date(
                                                currentDayChecking?.checkin_at
                                              )
                                            ) >= 1
                                              ? "0px"
                                              : "-25px",
                                        }}
                                        className={clsx({
                                          [classes.tooltipIcon]: dayIndex < 6,
                                          [classes.noSourceIcon]: dayIndex >= 6,
                                          [classes.withSourceIcon]:
                                            !!currentDayCheckingIcon,
                                        })}
                                      >
                                        <CustomTooltip
                                          className={`${
                                            isCurrentDayPaidAndUpcoming
                                              ? classes.paidSvgFill
                                              : isCurrentDayOwnerAndUpcoming
                                              ? classes.ownerSvgFill
                                              : isCurrentDayPaidAndPrevious
                                              ? classes.paidPreviousSvgFill
                                              : classes.ownerPreviousSvgFill
                                          }`}
                                          tooltip={getTooltipContent(
                                            currentDayChecking
                                          )}
                                        />
                                      </div>
                                    ) : (
                                      <Typography
                                        variant="text-md"
                                        weight="medium"
                                        className={classes.name}
                                      >
                                        {currentDayChecking?.created_by
                                          ? ownerBlockName
                                          : currentDayChecking?.guest_name.split(
                                              " "
                                            )[0]}
                                      </Typography>
                                    )
                                  ) : differenceInDays(
                                      new Date(currentDayChecking?.checkout_at),
                                      new Date(currentDayChecking?.checkin_at)
                                    ) < 1 ? (
                                    <div
                                      style={{
                                        marginLeft:
                                          differenceInDays(
                                            new Date(
                                              currentDayChecking?.checkout_at
                                            ),
                                            new Date(
                                              currentDayChecking?.checkin_at
                                            )
                                          ) >= 1
                                            ? "0px"
                                            : "-25px",
                                      }}
                                      className={clsx({
                                        [classes.tooltipIcon]: dayIndex < 6,
                                        [classes.noSourceIcon]: dayIndex >= 6,
                                        [classes.withSourceIcon]:
                                          !!currentDayCheckingIcon,
                                      })}
                                    >
                                      <CustomTooltip
                                        className={`${
                                          isCurrentDayPaidAndUpcoming
                                            ? classes.paidSvgFill
                                            : isCurrentDayOwnerAndUpcoming
                                            ? classes.ownerSvgFill
                                            : isCurrentDayPaidAndPrevious
                                            ? classes.paidPreviousSvgFill
                                            : classes.ownerPreviousSvgFill
                                        }`}
                                        tooltip={getTooltipContent(
                                          currentDayChecking
                                        )}
                                      />
                                    </div>
                                  ) : (
                                    dayIndex < 6 && (
                                      <ReservationDetails
                                        currentDayChecking={currentDayChecking}
                                        isCurrentDayOwnerAndUpcoming={
                                          isCurrentDayOwnerAndUpcoming
                                        }
                                        isCurrentDayPaidAndUpcoming={
                                          isCurrentDayPaidAndUpcoming
                                        }
                                      />
                                    )
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                          {isSecondOfRange &&
                            differenceInCalendarDays(
                              new Date(
                                currentDayReservations?.[0]?.checkout_at
                              ),
                              new Date(currentDayReservations?.[0]?.checkin_at)
                            ) >= 2 &&
                            dayIndex === 0 && (
                              <>
                                <ChannelIcon
                                  isCurrentDayPaidAndUpcoming={
                                    isCurrentDayPaidAndUpcoming
                                  }
                                  isCurrentDayOwnerAndUpcoming={
                                    isCurrentDayOwnerAndUpcoming
                                  }
                                  isCurrentDayPaidAndPrevious={
                                    isCurrentDayPaidAndPrevious
                                  }
                                  currentDayCheckingIcon={channelToIconMap(
                                    currentDayReservations?.[0]?.source
                                  )}
                                />
                                <div className={classes.reservationContent}>
                                  <ReservationDetails
                                    currentDayChecking={
                                      currentDayReservations?.[0]
                                    }
                                    isCurrentDayOwnerAndUpcoming={
                                      isCurrentDayOwnerAndUpcoming
                                    }
                                    isCurrentDayPaidAndUpcoming={
                                      isCurrentDayPaidAndUpcoming
                                    }
                                  />
                                </div>
                              </>
                            )}
                        </div>
                        {currentDayReservations?.length > 1 && (
                          <div
                            //todo refactor this
                            className={clsx(
                              classes.reservation,
                              isPaidCheckout
                                ? checkoutDateFirst &&
                                  checkoutDateFirst >= today
                                  ? classes.paid
                                  : classes.paidPrevious
                                : checkoutDateFound &&
                                  checkoutDateFound >= today
                                ? classes.owner
                                : classes.ownerPrevious,
                              currentDayReservations?.length > 1 &&
                                classes.bothEnding,
                              isPaidCheckin &&
                                todayCheckinDate &&
                                todayCheckoutDate &&
                                todayCheckoutDate > today &&
                                todayCheckinDate <= today &&
                                classes.paidCurrent,
                              !isPaidCheckin &&
                                todayCheckinDate &&
                                todayCheckoutDate &&
                                todayCheckoutDate > today &&
                                todayCheckinDate <= today &&
                                classes.ownerCurrent
                            )}
                          />
                        )}
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
      {!small && <Legend legendData={calendarLegend} />}
    </>
  );
};
